import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, PauseCircle, Pencil, Play, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { API, api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PurgeIssuesDialog } from "@/components/issues/PurgeIssuesDialog";

const FIELDS = [
  ["name", "Project name", true],
  ["code", "Project code", true],
  ["location", "Location", false],
  ["client", "Client", false],
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1690944851207-3f288c8fcd0b?crop=entropy&cs=srgb&fm=jpg&w=600&q=80";

export function projectImage(project) {
  if (project.has_thumbnail)
    return `${API}/projects/${project.id}/thumbnail?v=${project.thumbnail_version || ""}`;
  return project.thumbnail_url || FALLBACK_IMAGE;
}

const EMPTY = { name: "", code: "", location: "", client: "", thumbnail_url: "" };

/** Admin-only project management: create, edit every field, replace the image. */
export function ProjectManager({ projects, onChanged }) {
  const [editing, setEditing] = useState(null); // project id or "__new__"
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [purgeProject, setPurgeProject] = useState(null);
  const fileRefs = useRef({});

  const startEdit = (project) => {
    setEditing(project.id);
    setForm({
      name: project.name || "",
      code: project.code || "",
      location: project.location || "",
      client: project.client || "",
      thumbnail_url: project.thumbnail_url || "",
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "__new__") await api.post("/projects", form);
      else await api.put(`/projects/${editing}`, form);
      toast.success("Project saved");
      setEditing(null);
      setForm(EMPTY);
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (projectId, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/projects/${projectId}/thumbnail`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Project image updated");
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const setStatus = async (project) => {
    const next = project.status === "suspended" ? "active" : "suspended";
    try {
      await api.put(`/projects/${project.id}/status`, { status: next });
      toast.success(next === "suspended" ? "Project suspended" : "Project reactivated");
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/projects/${confirmDelete.id}`);
      toast.success(`Project ${confirmDelete.code} deleted`);
      setConfirmDelete(null);
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const editor = (
    <div
      data-testid="project-editor"
      className="space-y-3 rounded-xl border border-cyan-500/30 bg-white p-5 okn-card-shadow"
    >
      <p className="font-display text-sm font-medium">
        {editing === "__new__" ? "New project" : "Edit project"}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {FIELDS.map(([key, label, required]) => (
          <div key={key}>
            <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              {label}
              {required ? " *" : ""}
            </p>
            <Input
              data-testid={`project-field-${key}`}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              className="border-slate-200 bg-[#F8FAFC] text-sm"
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            Image URL (optional — or upload a file below)
          </p>
          <Input
            data-testid="project-field-thumbnail_url"
            value={form.thumbnail_url}
            onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
            placeholder="https://…"
            className="mono border-slate-200 bg-[#F8FAFC] text-xs"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          data-testid="project-save-button"
          onClick={save}
          disabled={busy || !form.name.trim() || !form.code.trim()}
          className="h-9 rounded-full bg-cyan-600 px-5 text-xs text-white hover:bg-cyan-700"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Save"}
        </Button>
        <Button
          data-testid="project-cancel-button"
          variant="ghost"
          onClick={() => setEditing(null)}
          className="h-9 rounded-full text-xs text-slate-600"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="project-manager">
      {editing === "__new__" ? (
        editor
      ) : (
        <Button
          data-testid="admin-new-project-button"
          onClick={() => {
            setEditing("__new__");
            setForm(EMPTY);
          }}
          className="rounded-full bg-cyan-600 text-xs text-white hover:bg-cyan-700"
        >
          <Plus size={15} className="mr-1" /> New project
        </Button>
      )}

      {projects.map((project) =>
        editing === project.id ? (
          <div key={project.id}>{editor}</div>
        ) : (
          <div
            key={project.id}
            data-testid={`admin-project-${project.code}`}
            className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 okn-card-shadow"
          >
            <img
              src={projectImage(project)}
              alt={project.name}
              className="h-16 w-24 shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">{project.name}</p>
              <p className="mono text-[11px] text-slate-500">
                {project.code} · {project.location || "—"} · {project.client || "—"}
              </p>
              <p className="mono text-[10px] text-slate-400">
                Revision {project.active_revision_name || "—"} · {project.panel_count || 0} panels
              </p>
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              <span
                className={`mono rounded-full px-2.5 py-1 text-[9px] uppercase tracking-wider ${
                  project.status === "suspended"
                    ? "bg-amber-500/15 text-amber-700"
                    : "bg-emerald-500/15 text-emerald-700"
                }`}
              >
                {project.status || "active"}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid={`project-image-input-${project.code}`}
                ref={(el) => {
                  fileRefs.current[project.id] = el;
                }}
                onChange={(e) => uploadImage(project.id, e)}
              />
              <button
                data-testid={`project-image-button-${project.code}`}
                onClick={() => fileRefs.current[project.id]?.click()}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
              >
                <ImagePlus size={12} /> Image
              </button>
              <button
                data-testid={`project-edit-button-${project.code}`}
                onClick={() => startEdit(project)}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                data-testid={`project-suspend-button-${project.code}`}
                onClick={() => setStatus(project)}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-amber-500/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-amber-700 hover:bg-amber-500/10"
              >
                {project.status === "suspended" ? (
                  <>
                    <Play size={12} /> Reactivate
                  </>
                ) : (
                  <>
                    <PauseCircle size={12} /> Suspend
                  </>
                )}
              </button>
              <button
                data-testid={`project-reset-statuses-button-${project.code}`}
                onClick={() => setPurgeProject({ ...project, mode: "statuses" })}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700 hover:border-red-400 hover:text-red-700"
              >
                <RotateCcw size={12} /> Statuses
              </button>
              <button
                data-testid={`project-reset-logs-button-${project.code}`}
                onClick={() => setPurgeProject({ ...project, mode: "logs" })}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700 hover:border-red-400 hover:text-red-700"
              >
                <RotateCcw size={12} /> Logs
              </button>
              <button
                data-testid={`project-purge-comments-button-${project.code}`}
                onClick={() => setPurgeProject({ ...project, mode: "comments" })}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-amber-500/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-amber-700 hover:bg-amber-500/10"
              >
                <Trash2 size={12} /> Comments
              </button>
              <button
                data-testid={`project-purge-issues-button-${project.code}`}
                onClick={() => setPurgeProject({ ...project, mode: "issues" })}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-red-500/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-red-700 hover:bg-red-500/10"
              >
                <Trash2 size={12} /> Issues
              </button>
              <button
                data-testid={`project-delete-button-${project.code}`}
                onClick={() => setConfirmDelete(project)}
                className="okn-tool flex items-center gap-1.5 rounded-full border border-red-500/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-red-700 hover:bg-red-500/10"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        )
      )}

      {purgeProject ? (
        <PurgeIssuesDialog
          projectId={purgeProject.id}
          projectCode={purgeProject.code}
          mode={purgeProject.mode}
          total={purgeProject.open_issues ?? "all"}
          open
          onOpenChange={(next) => !next && setPurgeProject(null)}
          onDone={() => {
            setPurgeProject(null);
            onChanged?.();
          }}
        />
      ) : null}

      {confirmDelete ? (
        <div
          data-testid="delete-confirm-dialog"
          className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4"
        >
          <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-white p-6 okn-card-shadow">
            <p className="font-display text-lg font-semibold text-slate-900">
              Delete {confirmDelete.code}?
            </p>
            <p className="mt-2 text-sm text-slate-600">
              This permanently removes the project, its model revisions, panels, workflow
              statuses, history, issues and pallets. This cannot be undone — use
              <b> Suspend</b> if you only want to hide it.
            </p>
            <div className="mt-5 flex gap-2">
              <Button
                data-testid="delete-cancel-button"
                variant="ghost"
                onClick={() => setConfirmDelete(null)}
                className="h-9 flex-1 rounded-full text-xs text-slate-600"
              >
                Cancel
              </Button>
              <Button
                data-testid="delete-confirm-button"
                onClick={doDelete}
                className="h-9 flex-1 rounded-full bg-red-600 text-xs text-white hover:bg-red-700"
              >
                <Trash2 size={13} className="mr-1.5" /> Delete permanently
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
